import type { AssetLocaleCurrency } from '../utils/assetCountryCurrency';
import {
  isPriceRelatedFieldKey,
  labelWithCurrencySymbol,
} from '../utils/assetCountryCurrency';
import type { MessageKey } from './messages';
import type { TranslateFn } from './operationLabels';

function normalizeFieldKey(key: string): string {
  return key.trim().toLowerCase().replace(/\s+/g, '_');
}

/** API / JSON field names → i18n message keys. */
const FIELD_MESSAGE_KEY: Record<string, MessageKey> = {
  id: 'assetField.id',
  asset_id: 'assetField.assetId',
  asset_name: 'assetField.assetName',
  name: 'assetField.name',
  facility_name: 'assetField.facilityName',
  description: 'assetField.description',
  sub_title: 'assetField.subTitle',
  subtitle: 'assetField.subTitle',
  subTitle: 'assetField.subTitle',
  email: 'assetField.email',
  phone_number: 'assetField.phoneNumber',
  phone_number_2: 'assetField.phoneNumber2',
  phone: 'assetField.phone',
  registration_date: 'assetField.registrationDate',
  registered_at: 'assetField.registeredAt',
  created_at: 'assetField.createdAt',
  updated_at: 'assetField.updatedAt',
  deleted_at: 'assetField.deletedAt',
  state_id: 'assetField.stateId',
  country_id: 'assetField.countryId',
  region_id: 'assetField.regionId',
  district_id: 'assetField.districtId',
  asset_type_id: 'assetField.assetTypeId',
  asset_type_name: 'assetField.assetTypeName',
  asset_status: 'assetField.assetStatus',
  avg_rating: 'assetField.avgRating',
  average_rating: 'assetField.averageRating',
  reviews_count: 'assetField.reviewsCount',
  review_count: 'assetField.reviewCount',
  country_name: 'assetField.countryName',
  state_name: 'assetField.stateName',
  region_name: 'assetField.regionName',
  district_name: 'assetField.districtName',
  pricing_type_name: 'assetField.pricingTypeName',
  facility_type_name: 'assetField.facilityTypeName',
  type_name: 'assetField.typeName',
  latitude: 'assetField.latitude',
  longitude: 'assetField.longitude',
  min_capacity: 'assetField.minCapacity',
  max_capacity: 'assetField.maxCapacity',
  capacity: 'assetField.capacity',
  street: 'assetField.street',
  street_number: 'assetField.streetNumber',
  house_number: 'assetField.houseNumber',
  city: 'assetField.city',
  zip_code: 'assetField.zipCode',
  postal_code: 'assetField.postalCode',
  zip: 'assetField.zip',
  state: 'assetField.state',
  country: 'assetField.country',
  address: 'assetField.address',
  address_line1: 'assetField.addressLine1',
  address_line2: 'assetField.addressAdditional',
  address2: 'assetField.addressAdditional',
  address_2: 'assetField.addressAdditional',
  is_verified_phone: 'assetField.isVerifiedPhone',
  is_verified_email: 'assetField.isVerifiedEmail',
  isverifiedphone: 'assetField.isVerifiedPhone',
  isverifiedemail: 'assetField.isVerifiedEmail',
  building: 'assetField.building',
  floor: 'assetField.floor',
  apartment: 'assetField.apartment',
  price: 'assetField.price',
  unit_price: 'assetField.unitPrice',
  amount: 'assetField.amount',
  cost: 'assetField.cost',
  rate: 'assetField.rate',
  fee: 'assetField.fee',
  addon_price: 'assetField.addonPrice',
  available_quantity: 'assetField.availableQuantity',
  quantity: 'assetField.quantity',
  pricing_type_id: 'assetField.pricingTypeId',
  pricing_type: 'assetField.pricingType',
  type: 'assetDetail.type',
  item: 'assetDetail.item',
  facility_id: 'assetField.facilityId',
  addon_id: 'assetField.addonId',
  facility_type_id: 'assetField.facilityTypeId',
  addon_name: 'assetField.addonName',
  title: 'assetField.title',
  label: 'assetField.label',
  status: 'assetField.status',
  active: 'assetField.active',
  published: 'assetField.published',
  owner_id: 'assetField.ownerId',
  user_id: 'assetField.userId',
  worker_id: 'assetField.workerId',
  first_name: 'assetField.firstName',
  last_name: 'assetField.lastName',
  contact_first_name: 'register.contactFirstName',
  contact_last_name: 'register.contactLastName',
  business_name: 'assetField.businessName',
  business_id: 'assetField.businessId',
  business_client_identifier: 'register.businessClientWorkerCode',
  business_type: 'assetField.businessType',
  credits: 'assetField.credits',
  available_credits: 'profile.field.availableCredits',
  credit_balance: 'profile.field.availableCredits',
  credits_balance: 'profile.field.availableCredits',
  remaining_credits: 'profile.field.availableCredits',
  employer_credits: 'profile.field.availableCredits',
  employer_credit_balance: 'profile.field.availableCredits',
  credits_per_employee: 'assetField.creditsPerEmployee',
  is_business: 'assetField.isBusiness',
  national_id: 'assetField.nationalId',
  occupation: 'profile.field.occupation',
  birth_date: 'assetField.birthDate',
  date_of_birth: 'assetField.birthDate',
  dob: 'assetField.birthDate',
  gender: 'assetField.gender',
  position: 'assetField.position',
  role_id: 'assetField.roleId',
  is_active: 'assetField.isActive',
  worker_status: 'assetField.workerStatus',
  employment_status: 'assetField.employmentStatus',
  line1: 'assetField.addressLine1',
  line2: 'assetField.addressAdditional',
  postalcode: 'assetField.postalCode',
  password: 'assetField.password',
  confirm_password: 'assetField.confirmPassword',
  old_password: 'assetField.oldPassword',
  new_password: 'assetField.newPassword',
  images: 'assetField.images',
  opening_times: 'assetField.openingTimes',
  facilities: 'assetField.facilities',
  pricing_methods: 'assetField.pricingMethods',
  addons: 'assetField.addons',
  currency: 'assetField.currency',
  vat: 'assetField.vat',
  tax: 'assetField.tax',
  notes: 'assetField.notes',
  website: 'assetField.website',
  url: 'assetField.url',
};

const WEEKDAY_MESSAGE_KEY: Record<string, MessageKey> = {
  sunday: 'weekday.sunday',
  monday: 'weekday.monday',
  tuesday: 'weekday.tuesday',
  wednesday: 'weekday.wednesday',
  thursday: 'weekday.thursday',
  friday: 'weekday.friday',
  saturday: 'weekday.saturday',
};

/** Order for nested profile/worker address objects (city, then street, then additional info). */
const ADDRESS_FIELD_ORDER = [
  'city',
  'street',
  'building',
  'street_number',
  'line1',
  'address_line1',
  'house_number',
  'address2',
  'address_2',
  'line2',
  'zip_code',
  'postal_code',
  'postalcode',
  'state',
  'country',
] as const;

export function isAddressFieldGroup(keys: string[]): boolean {
  const normalized = new Set(keys.map((k) => normalizeFieldKey(k)));
  return normalized.has('street') || normalized.has('address2') || normalized.has('line1');
}

export function sortAddressFieldKeys(keys: string[]): string[] {
  const orderIndex = new Map<string, number>(
    ADDRESS_FIELD_ORDER.map((k, i) => [k, i]),
  );
  return [...keys].sort((a, b) => {
    const ai = orderIndex.get(normalizeFieldKey(a)) ?? 999;
    const bi = orderIndex.get(normalizeFieldKey(b)) ?? 999;
    if (ai !== bi) {
      return ai - bi;
    }
    return a.localeCompare(b);
  });
}

/** Create / update asset JSON roots: name & description first, geo before address. */
const ASSET_MUTATION_ROOT_ORDER = [
  'asset_name',
  'name',
  'description',
  'sub_title',
  'asset_type_id',
  'country_id',
  'state_id',
  'region_id',
  'district_id',
  'address',
  'phone_number',
  'phone',
  'email',
  'min_capacity',
  'max_capacity',
] as const;

function isAssetMutationRoot(keys: string[]): boolean {
  const normalized = new Set(keys.map((k) => normalizeFieldKey(k)));
  return normalized.has('asset_name') && normalized.has('asset_type_id');
}

function sortByPreferredOrder(keys: string[], order: readonly string[]): string[] {
  const orderIndex = new Map(order.map((k, i) => [normalizeFieldKey(k), i]));
  return [...keys].sort((a, b) => {
    const ai = orderIndex.get(normalizeFieldKey(a)) ?? 999;
    const bi = orderIndex.get(normalizeFieldKey(b)) ?? 999;
    if (ai !== bi) {
      return ai - bi;
    }
    return a.localeCompare(b);
  });
}

const REGISTER_PRIVATE_CLIENT_ROOT_ORDER = [
  'email',
  'password',
  'confirm_password',
  'first_name',
  'last_name',
  'birth_date',
  'occupation',
  'phone_number',
] as const;

const REGISTER_BUSINESS_CLIENT_ROOT_ORDER = [
  'business_name',
  'email',
  'password',
  'confirm_password',
  'contact_first_name',
  'contact_last_name',
  'phone_number',
  'business_id',
  'credits_per_employee',
  'country_id',
  'state_id',
] as const;

const REGISTER_BUSINESS_WORKER_ROOT_ORDER = [
  'business_client_identifier',
  'email',
  'password',
  'confirm_password',
  'first_name',
  'last_name',
  'phone_number',
  'national_id',
  'birth_date',
  'position',
] as const;

const REGISTER_ASSET_OWNER_ROOT_ORDER = [
  'business_name',
  'business_type',
  'email',
  'password',
  'confirm_password',
  'contact_first_name',
  'contact_last_name',
  'phone_number',
  'phone_number_2',
  'business_id',
  'country_id',
  'state_id',
] as const;

function isPrivateClientRegisterRoot(keys: string[]): boolean {
  const normalized = new Set(keys.map((k) => normalizeFieldKey(k)));
  return (
    normalized.has('first_name') &&
    normalized.has('birth_date') &&
    !normalized.has('business_name') &&
    !normalized.has('position')
  );
}

function isBusinessClientRegisterRoot(keys: string[]): boolean {
  const normalized = new Set(keys.map((k) => normalizeFieldKey(k)));
  return (
    normalized.has('business_name') &&
    normalized.has('business_id') &&
    normalized.has('credits_per_employee') &&
    !normalized.has('position')
  );
}

function isBusinessWorkerRegisterRoot(keys: string[]): boolean {
  const normalized = new Set(keys.map((k) => normalizeFieldKey(k)));
  return (
    normalized.has('position') &&
    normalized.has('first_name') &&
    normalized.has('national_id') &&
    !normalized.has('business_name')
  );
}

function isAssetOwnerRegisterRoot(keys: string[]): boolean {
  const normalized = new Set(keys.map((k) => normalizeFieldKey(k)));
  return (
    normalized.has('contact_first_name') &&
    normalized.has('contact_last_name') &&
    !normalized.has('first_name')
  );
}

export function sortRecordKeysForDisplay(path: readonly string[], keys: string[]): string[] {
  if (path.length === 1 && normalizeFieldKey(path[0]) === 'address') {
    return sortAddressFieldKeys(keys);
  }
  if (path.length === 0 && isAddressFieldGroup(keys)) {
    return sortAddressFieldKeys(keys);
  }
  if (path.length === 0 && isAssetMutationRoot(keys)) {
    return sortByPreferredOrder(keys, ASSET_MUTATION_ROOT_ORDER);
  }
  if (path.length === 0 && isPrivateClientRegisterRoot(keys)) {
    return sortByPreferredOrder(keys, REGISTER_PRIVATE_CLIENT_ROOT_ORDER);
  }
  if (path.length === 0 && isBusinessClientRegisterRoot(keys)) {
    return sortByPreferredOrder(keys, REGISTER_BUSINESS_CLIENT_ROOT_ORDER);
  }
  if (path.length === 0 && isBusinessWorkerRegisterRoot(keys)) {
    return sortByPreferredOrder(keys, REGISTER_BUSINESS_WORKER_ROOT_ORDER);
  }
  if (path.length === 0 && isAssetOwnerRegisterRoot(keys)) {
    return sortByPreferredOrder(keys, REGISTER_ASSET_OWNER_ROOT_ORDER);
  }
  return [...keys].sort();
}

export function localizeGenderValue(value: unknown, t: TranslateFn): string {
  if (value === null || value === undefined) {
    return '—';
  }
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  switch (raw) {
    case 'male':
      return t('profile.gender.male');
    case 'female':
      return t('profile.gender.female');
    case 'other':
      return t('profile.gender.other');
    default:
      return typeof value === 'string' && value.trim() ? value.trim() : '—';
  }
}

export function localizeAssetFieldLabel(key: string, t: TranslateFn): string {
  const normalized = normalizeFieldKey(key);
  const msgKey = FIELD_MESSAGE_KEY[normalized];
  if (msgKey) {
    return t(msgKey);
  }
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Field label with currency symbol for price columns (e.g. מחיר (₪)). */
export function localizeAssetPriceFieldLabel(
  key: string,
  t: TranslateFn,
  currency: AssetLocaleCurrency | null,
): string {
  const base = localizeAssetFieldLabel(key, t);
  if (currency && isPriceRelatedFieldKey(key)) {
    return labelWithCurrencySymbol(base, currency);
  }
  return base;
}

export function localizeWeekdayLabel(dayKey: string, t: TranslateFn): string {
  const normalized = normalizeFieldKey(dayKey);
  const msgKey = WEEKDAY_MESSAGE_KEY[normalized];
  if (msgKey) {
    return t(msgKey);
  }
  return localizeAssetFieldLabel(dayKey, t);
}

export function localizeFacilityAddonType(value: unknown, t: TranslateFn): string {
  if (value === 'Facility') {
    return t('assetDetail.facility');
  }
  if (value === 'Add-on') {
    return t('assetDetail.addon');
  }
  return String(value ?? '—');
}
